import { useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import {
  getLocales,
  withContext,
  withUseFormHook,
} from "@/common/utils/withContext";
import { EvesModal } from "@/common/components/molecules/modal/modal";
import {
  DefaultPageSize,
  HTTPError,
  StatusCodes,
} from "@/common/constants/constants";
import AccountBoxOutlinedIcon from "@mui/icons-material/AccountBoxOutlined";
import { useMutation, useQuery } from "react-query";
import { NotifyType } from "@/common/utils/notificationService";
import { connect } from "react-redux";
import { USER_STATUSES } from "@/modules/profile/shared/models/user";
import {
  canListPaymentMethods,
  getAvailableRolesFromIncludeRoles,
  getRoleOptionFromRoleString,
  handleHttpError,
  isActive,
  isSuperAdmin,
} from "@/common/utils/utils";
import {
  ComponentKeys,
  LookupEntityType,
  UpdatedUserRole,
  UserStatus
} from "@/common/enums/enums";
import { getComponentValueSafe } from "@/common/utils/componentListHelper";
import {
  createUser,
  getUserInformation,
  updateUser,
} from "@/services/userProfile";
import {
  Email,
  LocationOn,
  PaymentOutlined,
} from "@mui/icons-material";
import { formSchema } from "@/modules/user/shared/schemas/userSchema";
import DefaultAvatar from "@/assets/img/user-default.png";
import { IUserFormPropsModel, UserFormModel } from "../../models/user";
import Notifications from "@/modules/shared/components/user/notifications";
import Address from "@/modules/shared/components/user/address";
import PaymentMethods from "@/modules/shared/components/user/paymentMethods";
import UserProfile from "@/modules/shared/components/user/userProfile";
import { useTranslation } from "react-i18next";
import store from "@/store";
import { setLoader } from "@/store/app/reducer";
import LookupComponent from "@/common/components/lookupComponent";
import { getUserSites, assignSite, unAssignSite, checkIsAdminRole } from "@/services/users";

const UserForm = (props: IUserFormPropsModel) => {
  const { t: translate } = useTranslation();
  const [isSiteLookupOpen, setSiteLookupOpen] = useState(false);
  const userForm = props.formService as UseFormReturn<UserFormModel, any>;
  const userName = userForm.watch("name");
  const userFirstName = userForm.watch("firstName");
  const currentAssignedSites = userForm.watch("assignedSites") || [];
  const isUserSuperAdmin = isSuperAdmin();
  const isBillingComponentActive = isActive(getComponentValueSafe(ComponentKeys.BILLING));
  const isUserCanListPaymentMethods: any = canListPaymentMethods();

  // EVES-5268: Validate individual mandatory fields
  // Author: Abhishek Bharti
  // Date: 08-04-2026
  const userInfo: any = localStorage.getItem("user_info");
  const userInfoData = userInfo ? JSON.parse(userInfo) : {};
  const includeRoles = userInfoData.includeRole ?? [];


  const [state, setStateData] = useState({
    selectedTab: "1",
    limit: DefaultPageSize,
    canUpdate: false,
    isAccountLookupModalOpen: false,
    userData: {},
    avatar: DefaultAvatar,
    isTechnicalDisabled: false,
    isFreeAccessDisabled: false,
    userRoleOptions: [],
    notificationsActive: true,
    isAdminNotificationEnabled: false,
    isNotificationEnabled: true,
    isEnableAllEnabled: false,
    userRole: "",
    securityState: true,
    miscsState: true,
    canUserListPaymentMethods: true,
    isRoleDisabled: false,
    isSiteSelectionDisabled: false,
    // EVES-5268: Stores API result for the currently selected role
    isSelectedRoleAdmin: false,
  } as any);

  const notify: NotifyType = {
    message: "",
    type: "success",
  };

  // EVES-5268: Dynamic admin role check from check-isadmin-role API
  // Author: Abhishek Bharti
  // Date: 08-04-2026
  const isAdmin = state.isSelectedRoleAdmin;
  const isSiteRequired = !isAdmin; // Site is required only when isAdmin is false

  // EVES-5268: Valid if array has at least 1 site with a non-empty ID
  // Author: Abhishek Bharti
  // Date: 08-04-2026
  const isSiteValid = isSiteRequired
    ? Array.isArray(currentAssignedSites) &&
    currentAssignedSites.length > 0 &&
    currentAssignedSites.some((s: { id?: string }) => s && s.id && s.id.trim() !== "")
    : true;

  // EVES-5268: Use formSchema's built-in isValid + site validation
  // Author: Abhishek Bharti
  // Date: 08-04-2026
  const isFormValid = props.userId
    ? userForm.formState.isValid && isSiteValid
    : userForm.formState.isValid && userForm.formState.isDirty && isSiteValid;

  const convertObject = (userOptions: any) => {
    const options: any[] = [];
    for (const pair of userOptions) {
      options.push({ label: pair.value, key: pair.key, value: pair.value });
    }
    return options;
  };

  const statusOptions = convertObject(USER_STATUSES).map((option) => ({
    ...option,
    label: translate(option.label || " "),
  }));

  const userLocales: any = getLocales();
  const languageOptions = convertObject(userLocales);

  const getRole = (userRoleOptions: any[], role: string) => {
    const userRole: any = userRoleOptions.find(
      (userRole) => userRole.key === role
    );
    return userRole?.value;
  };

  const getKey = (options: any[], value: string) => {
    const key: any = options.find((data) => data.value === value)?.key;
    return key;
  };

  const getStatus = (statusKey: string) => {
    const statusValue = statusOptions.find(
      (status) => status.key == statusKey
    ).value;
    return statusValue;
  };

  const getRoleKey = (userRoleOptions: any[], value: string) => {
    const userRole: any = userRoleOptions.find(
      (userRole) => userRole.value === value
    );
    return userRole.key;
  };

  const getLanguage = (locale: string) => {
    const userLanguage: any = languageOptions.find(
      (userLanguage) => userLanguage.key === locale
    );
    return userLanguage.value;
  };

  const notificationControls: any[] = [
    "sendSessionStarted",
    "sendOptimalChargeReached",
    "sendEndOfCharge",
    "sendEndOfSession",
    "sendUserAccountStatusChanged",
    "sendSessionNotStarted",
    "sendUserAccountInactivity",
    "sendBillingNewInvoice",
    "sendPreparingSessionNotStarted",
  ];

  const adminNotificationControls: any[] = [
    "sendUnknownUserBadged",
    "sendUnknownUserBadged",
    "sendChargingStationStatusError",
    "sendOfflineChargingStations",
    "sendOcpiPatchStatusError",
    "sendOicpPatchStatusError",
    "sendBillingSynchronizationFailed",
    "sendBillingPeriodicOperationFailed",
    "sendComputeAndApplyChargingProfilesFailed",
    "sendEndUserErrorNotification",
    "sendChargingStationRegistered",
    "sendAdminAccountVerificationNotification",
  ];

  // --- NOTIFICATION LOGIC ---
  const enableNotifications = (notificationControl: string, enabled: boolean) => {
    if (notificationControl === "adminNotificationControls") {
      setStateData((currentData: any) => {
        return {
          ...currentData,
          isAdminNotificationEnabled: enabled,
        };
      });
    } else if (notificationControl === "notificationControls") {
      setStateData((currentData: any) => {
        return {
          ...currentData,
          isNotificationEnabled: enabled,
        };
      });
    }
  };

  const enableAllNotifications = (enabled: boolean) => {
    enableNotifications("notificationControls", enabled);
    enableNotifications("adminNotificationControls", enabled);
  };

  const loadUser = (userData: any) => {
    const userFormData: UserFormModel = new UserFormModel();
    const userRoles: any[] = getAvailableRolesFromIncludeRoles(includeRoles); //  Role Mapping: for the dropdown to "Select" the correct value
    const userRoleOptions = convertObject(userRoles);
    const currentRoleOption = userRoleOptions.find(opt => opt.key === userData.role);
    userFormData.role = currentRoleOption ? currentRoleOption.value : userData.role;

    //  Site Mapping (Priority to the sites  fetched separately)
    if (userData?.assignedSites && userData?.assignedSites?.length > 0) {
      userFormData.assignedSites = userData.assignedSites;
    }
    else if (userData?.siteIDs && userData?.siteIDs?.length > 0) {
      // Check if it's a global user (siteIDs contains only an empty string "")
      if (userData?.siteIDs?.length === 1 && userData?.siteIDs?.[0] === "") {
        userFormData.assignedSites = [{ id: "", name: translate("general.all_sites") }];
      } else {
        userFormData.assignedSites = userData.siteIDs
          .filter((id: string) => id !== "")
          .map((id: string) => ({ id: id, name: id }));
      }
    }
    if (userData.id) {
      userFormData.id = userData.id;
    }
    userFormData.issuer = userData.issuer;
    if (userData.name) {
      userFormData.name = userData.name.toUpperCase();
    }
    if (userData.firstName) {
      userFormData.firstName = userData.firstName;
    }
    if (userData.email) {
      userFormData.email = userData.email;
      userFormData.originalEmail = userData.email;
    }
    if (userData.phone) {
      userFormData.phone = userData.phone;
    }
    if (userData.mobile) {
      userFormData.mobile = userData.mobile;
    }
    if (userData.status) {
      const status = getStatus(userData.status);
      userFormData.status = status;
    }
    if (userData.role) {
      // Filter roles based on includeRole from user_info
      const userRoles: any[] = getAvailableRolesFromIncludeRoles(includeRoles);
      const userRoleOptions: any[] = convertObject(userRoles);
      const currentRoleOption = getRoleOptionFromRoleString(userData.role);
      let isRoleDisabled = false;

      if (currentRoleOption) {
        const roleExists = userRoleOptions.some(
          (option) => option.key === currentRoleOption.key
        );
        if (!roleExists) {
          // Add the current role to the options
          userRoleOptions.push({
            label: currentRoleOption.value,
            key: currentRoleOption.key,
            value: currentRoleOption.value,
          });
          // Disable the role field if current role is not in includeRole
          isRoleDisabled = true;
        }
      }
      setStateData((currentData: any) => {
        return {
          ...currentData,
          userRoleOptions,
          isRoleDisabled,
        };
      });
      // Get the role value for the form, using the current role option if available
      const roleValue = getRole(userRoleOptions, userData.role);

      userFormData.role = roleValue || (currentRoleOption ? currentRoleOption.value : "");

      if (userData.role === UpdatedUserRole.ADMIN) {

        userFormData.role =
          roleValue || (currentRoleOption ? currentRoleOption.value : "");
        // EVES-4731: notification administration visibility to admin and cpo admin
        // Author: Abhishek Bharti
        // Date: 04/25/2026
        if ([UpdatedUserRole.ADMIN, UpdatedUserRole.CPO_ADMIN].includes(
          userData.role
        )) {

          setStateData((currentData: any) => {
            return {
              ...currentData,
              userRole: userData.role,
              showAdminNotification: true,
              showNotification: true,
              isNotificationEnabled: true,
              isAdminNotificationEnabled: true,
              isEnableAllEnabled: true,
            };
          });
        } else {
          setStateData((currentData: any) => {
            return {
              ...currentData,
              showAdminNotification: false,
              showNotification: true,
              isNotificationEnabled: true,
              isAdminNotificationEnabled: false,
              isEnableAllEnabled: true,
            };
          });
        }
      }

      if (userData.locale) {
        userFormData.language = getLanguage(userData.locale);
      }
      if (userData.plateID) {
        userFormData.plateID = userData.plateID;
      }
      if (userData.notificationsActive) {
        enableAllNotifications(userData.notificationsActive);
        userFormData.notificationsActive = userData.notificationsActive;
      } else {
        enableAllNotifications(userData.notificationsActive);
        userFormData.notificationsActive = userData.notificationsActive;
      }
      if (userData.projectFields.includes("technical")) {
        if (userData.technical) {
          userFormData.technical = userData.technical;
        }
      } else {
        setStateData((currentData: any) => {
          return {
            ...currentData,
            isTechnicalDisabled: true,
          };
        });
      }
      if (userData.projectFields.includes("freeAccess")) {
        if (userData.freeAccess) {
          userFormData.freeAccess = userData.freeAccess;
        }
      } else {
        setStateData((currentData: any) => {
          return {
            ...currentData,
            isFreeAccessDisabled: true,
          };
        });
      }
      if (userData.iNumber) {
        userFormData.iNumber = userData.iNumber;
      }
      if (userData.costCenter) {
        userFormData.costCenter = userData.costCenter;
      }
      // Address & Notifications (Nested mapping)
      userFormData.id = userData.id;
      userFormData.name = userData.name?.toUpperCase();
      userFormData.firstName = userData.firstName;
      userFormData.email = userData.email;
      userFormData.status = getStatus(userData.status);
      userFormData.language = getLanguage(userData.locale || "en_US");

      if (userData.role) {
        void roleChanged(userData.role, true);
      }
      userFormData.address = userData.address || userFormData.address;
      userFormData.notifications = userData.notifications || userFormData.notifications;
      userFormData.passwords = { password: "", repeatPassword: "" };
      return userFormData;
    }
  };

  const { refetch: fetchUserInformation } = useQuery(
    "User",
    async () => {
      const [userData, userSitesResponse] = await Promise.all([    //return getUserInformation(props.userId);
        getUserInformation(props.userId),
        getUserSites({ UserID: props.userId, Limit: 100 })
      ]);
      const assignedSites = userSitesResponse?.result?.map((res: any) => ({ // Extract site objects from the response
        id: res.site?.id,
        name: res.site?.name || res.site?.id
      })) || [];

      return { ...(userData as any), assignedSites };   // Return a merged object so loadUser can process it
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      enabled: false,
      onSettled: () => {
        store.dispatch(setLoader(false));
      },
      onSuccess: (data: any) => {
        if (data) {
          const userFormData = loadUser(data);
          //userForm.reset({ ...userFormData });
          setTimeout(() => {
            userForm.reset(userFormData as any);
          }, 50);
        }

      },
    }
  );

  const activateNotifications = (enabled: boolean) => {
    userForm.setValue("notificationsActive", enabled);
    setStateData((currentData: any) => {
      return {
        ...currentData,
        notificationsActive: enabled,
        isEnableAllEnabled: enabled,
      };
    });
  };

  const setNotificationsValue = (
    notificationControls: any[],
    enabled: boolean
  ) => {
    const formData: any = {};
    for (const notificationControl of notificationControls) {
      formData[notificationControl] = enabled;
    }
    const notificationData: any = {
      ...userForm.getValues().notifications,
      ...formData,
    };
    userForm.setValue("notifications", notificationData);
  };

  const setAllNotificationsValue = (enabled: boolean) => {
    setNotificationsValue(adminNotificationControls, enabled);
    setNotificationsValue(notificationControls, enabled);
  };

  const showAllNotifications = (enabled: boolean) => {
    showNotifications("notificationControls", enabled);
    showNotifications("adminNotificationControls", enabled);
  };

  const showNotifications = (notificationControl: string, enabled: boolean) => {
    if (notificationControl === "adminNotificationControls") {
      setStateData((currentData: any) => {
        return {
          ...currentData,
          showAdminNotification: enabled,
        };
      });
    } else if (notificationControl === "notificationControls") {
      setStateData((currentData: any) => {
        return {
          ...currentData,
          showNotification: enabled,
        };
      });
    }
  };
  const toggleNotifications = (checked: boolean) => {
    enableAllNotifications(checked);
  };

  // EVES-5268: Fetch admin role status on role selection (returns plain boolean)
  // Author: Abhishek Bharti
  // Date: 08-04-2026
  const fetchIsAdminForRole = async (role: string): Promise<boolean> => {
    if (!role) {
      return false;
    }
    try {
      const response = await checkIsAdminRole(role);
      return response === true;
    } catch (error) {
      console.error("Failed to check admin role:", error);
      return false;
    }
  };

  // EVES-5268: Call check-isadmin-role API on role change to drive site selection and validation
  // Author: Abhishek Bharti
  // Date: 08-04-2026
  const roleChanged = async (role: string, isInitialLoad: boolean = false) => {
    const isSuperAdminTarget = role === UpdatedUserRole.SUPER_ADMIN;
    const isAdminRole = isSuperAdminTarget || (await fetchIsAdminForRole(role));
    const isGlobalRole = isSuperAdminTarget || isAdminRole;

    if (!isInitialLoad) {
      const defaultSites = isAdminRole && !isSuperAdminTarget
        ? [{ id: "", name: translate("general.all_sites") }]
        : [];
      userForm.setValue("assignedSites", defaultSites, { shouldDirty: true });
    }
    setStateData((currentData: any) => ({
      ...currentData,
      isSelectedRoleAdmin: isAdminRole,
      isSiteSelectionHidden: isGlobalRole,
      isSiteSelectionDisabled: isAdminRole && !isSuperAdminTarget,
    }));
    switch (role) {

      case UpdatedUserRole.AGENT:
      case UpdatedUserRole.SITE_ADMIN:
      case UpdatedUserRole.SITE_OWNER:
        activateNotifications(true);
        showNotifications("notificationControls", true);
        enableNotifications("notificationControls", true);
        setNotificationsValue(notificationControls, true);
        enableNotifications("adminNotificationControls", false);
        showNotifications("adminNotificationControls", false);
        setNotificationsValue(adminNotificationControls, false);
        break;
      case UpdatedUserRole.ADMIN:
      case UpdatedUserRole.CPO_ADMIN:
        activateNotifications(true);
        enableAllNotifications(true);
        setAllNotificationsValue(true);
        showAllNotifications(true);
        break;
    }
  };
  const changeRole = (role: string) => {
    const key: string = getRoleKey(state.userRoleOptions, role);
    void roleChanged(key, false);
  };
  const loadUserInformation = async () => {
    try {
      if (props.userId) {
        store.dispatch(setLoader(true));
        await fetchUserInformation();
      } else {
        let userRoles: any[] = getAvailableRolesFromIncludeRoles(includeRoles);// Filter roles based on includeRole from user_info
        if (isUserSuperAdmin) {
          userRoles = userRoles.filter(r => r.key === UpdatedUserRole.SUPER_ADMIN);// Super Admin can only create other Super Admins
        } else {
          userRoles = userRoles.filter(r => r.key !== UpdatedUserRole.SUPER_ADMIN);
        }
        const userRoleOptions: any[] = convertObject(userRoles);
        setStateData((currentData: any) => {
          return {
            ...currentData,
            userRoleOptions,
            isRoleDisabled: false,
          };
        });
        const userModel = new UserFormModel();
        userModel.language = "English";
        // Set default role to first available role from includeRole, or "Basic" if none available
        const defaultRole =
          userRoleOptions.length > 0
            ? userRoleOptions[0].value



            : "";

        userModel.role = defaultRole;
        const defaultRoleKey =
          userRoleOptions.length > 0
            ? userRoleOptions[0].key
            : "";

        userModel.status = getStatus(UserStatus.ACTIVE);
        userModel.notifications.sendPreparingSessionNotStarted = false;
        userForm.reset({ ...userModel });
        await roleChanged(defaultRoleKey, true);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    loadUserInformation();
  }, []);

  // const setSecurityState = (recordState: any) => {
  //   setStateData((currentData: any) => {
  //     return {
  //       ...currentData,
  //       securityState: recordState,
  //     };
  //   });
  // };

  // const setMiscsState = (recordState: any) => {
  //   setStateData((currentData: any) => {
  //     return {
  //       ...currentData,
  //       miscsState: recordState,
  //     };
  //   });
  // };

  const isUserId = props.userId ? true : false;
  const onSiteSelect = (selectedSites: any[]) => {

    // Ensure we are storing an array of objects {id, name}
    const formattedSites = selectedSites.map(site => ({
      id: site.id,
      name: site.name || site.id
    }));

    userForm.setValue("assignedSites", formattedSites, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setSiteLookupOpen(false);
  };

  const handleResetPassword = () => {
    const email = userForm.getValues("email");
    // Call your service here
    props.notificationService?.notify({
      message: translate("users.reset_password"),
      type: "success",
    });
  };

  const tabsConfig = [
    {
      name: `User - ${userName} ${userFirstName}`,
      icon: <AccountBoxOutlinedIcon />,
      component: (
        <UserProfile
          userForm={userForm}
          onResetPassword={handleResetPassword} // prop for reset password
          isUpdate={!!props.userId} // Add this to tell the UI if we are editing
          isSiteSelectionHidden={state.isSiteSelectionHidden}
          isSiteSelectionDisabled={state.isSiteSelectionDisabled}
          onOpenSiteLookup={() => setSiteLookupOpen(true)}
          onChangeRole={changeRole}
          languageOptions={languageOptions}
          userRoleOptions={state.userRoleOptions}
          statusOptions={statusOptions}
          isTechnicalDisabled={state.isTechnicalDisabled}
          isFreeAccessDisabled={state.isFreeAccessDisabled}
          isRoleDisabled={state.isRoleDisabled}
          userId={props.userId}
          statusRequired={props.statusRequired}
          isSuperAdmin={props.isSuperAdmin} //super admin 
        />
      ),
      visible: true,
    },
    {
      name: `${translate("general.notifications")}`,
      icon: <Email />,
      component: (
        <Notifications
          userForm={userForm}
          toggleNotifications={toggleNotifications}
          notificationsActive={state.notificationsActive}
          showNotification={state.showNotification}
          showAdminNotification={state.showAdminNotification}
          isAdminNotificationEnabled={state.isAdminNotificationEnabled}
          isNotificationEnabled={state.isNotificationEnabled}
          isEnableAllEnabled={state.isEnableAllEnabled}
        />
      ),
      visible: true,
    },
    {
      name: `${translate("general.address")}`,
      icon: <LocationOn />,
      component: <Address userForm={userForm} />,
      visible: true,
    },
    {
      name: `${translate("users.tabs.billing")}`,
      icon: <PaymentOutlined />,
      component: <PaymentMethods userForm={userForm} />,
      visible:
        isUserId && isBillingComponentActive && isUserCanListPaymentMethods,
    },
  ];

  const { mutate: addUser } = useMutation(
    (payload: any) => {
      // Extract our internal UI flags before sending the actual data to the API
      const { _selectedSiteIDs, _isGlobalRole, ...apiPayload } = payload;
      // return both the API result and our flags so onSuccess can use them
      return createUser(apiPayload).then((res: any) => ({
        ...res,
        _selectedSiteIDs,
        _isGlobalRole,
      }));
    },
    {
      onMutate: () => {
        store.dispatch(setLoader(true));
      },
      onSettled: () => {
        store.dispatch(setLoader(false));
      },
      onSuccess: async (data: any) => {
        if (data.status == "Success") {
          const newUserId = data.id; // This is the ID of the user just created
          if (!data._isGlobalRole && data._selectedSiteIDs?.length > 0) {
            try {
              // await assignSite({ siteIDs: data._selectedSiteIDs }, newUserId);
            } catch (error: any) {
              if (error.response?.data?.errorMessage?.includes("E11000")) {
                console.warn("Site already assigned, skipping...");
              } else {
                console.error("Site assignment failed:", error);
              }
            }
          }
          props.onFormClose();
          props.resetUserForm("");
          notify.message = `${translate("users.create_success").replace(
            /{{userFullName}}/g,
            `'${userName} ${userFirstName}'`
          )}`;
          props.notificationService?.notify(notify);
          props.fetchAllUsers();
        } else {
          switch (data.status) {
            // Email already exists
            case HTTPError.USER_EMAIL_ALREADY_EXIST_ERROR:
              notify.message = `${translate(
                "authentication.email_already_exists"
              )}`;
              notify.type = "error";
              props.notificationService?.notify(notify);
              break;
            // User deleted
            case StatusCodes.NOT_FOUND:
              notify.message = `${translate("users.create_error")}`;
              notify.type = "error";
              props.notificationService?.notify(notify);
              break;
            default: {
              const errorNotify = handleHttpError(
                data,
                `${translate("users.create_error")}`
              );
              props.notificationService?.notify(errorNotify);
            }
          }
        }
      },
    }
  );

  // Helper to reconcile sites (Add new ones, remove old ones)
  const syncUserSites = async (userId: string, targetIDs: string[]) => {
    try {
      //  Get currently assigned sites
      const currentRes = await getUserSites({ UserID: userId, Limit: 100 });
      const currentIDs = currentRes.result.map((res: any) => res.site?.id).filter(Boolean);

      // Calculate Delta
      const toAdd = targetIDs.filter(id => !currentIDs.includes(id));
      const toRemove = currentIDs.filter(id => !targetIDs.includes(id));

      // Execute Updates sequentially to avoid DB locks
      if (toAdd.length > 0) {
        await assignSite({ siteIDs: toAdd }, userId);
      }
      if (toRemove.length > 0) {
        // ignore the dummy ID here if it was accidentally added
        const cleanToRemove = toRemove.filter(id => id.length === 24 && !id.startsWith('f000'));
        if (cleanToRemove.length > 0) {
          await unAssignSite({ siteIDs: cleanToRemove }, userId);
        }
      }
    } catch (error) {
      console.error("Site Sync Failed:", error);
    }
  };


  const { mutate: editUser } = useMutation(
    (payload: any) => {
      const { _selectedSiteIDs, _isGlobalRole, ...apiPayload } = payload;

      return updateUser(apiPayload).then((res: any) => ({
        ...res,
        _selectedSiteIDs,
        _isGlobalRole,
      }));
    },
    {
      onMutate: () => {
        store.dispatch(setLoader(true));
      },
      onSettled: () => {
        store.dispatch(setLoader(false));
      },
      onSuccess: async (data: any) => {
        if (data.status == "Success") {
          //  sync the sites using the specialized endpoint
          if (!data._isGlobalRole) {
            await syncUserSites(props.userId, data._selectedSiteIDs || []);
          }
          props.onFormClose();
          props.resetUserForm("");
          notify.message = `${translate("users.update_success").replace(
            /{{userFullName}}/g,
            `'${userName} ${userFirstName}'`
          )}`;
          props.notificationService?.notify(notify);
          props.fetchAllUsers();
        } else {
          switch (data.status) {
            // Email already exists
            case HTTPError.USER_EMAIL_ALREADY_EXIST_ERROR:
              notify.message = `${translate(
                "authentication.email_already_exists"
              )}`;
              notify.type = "error";
              props.notificationService?.notify(notify);
              break;
            // User deleted
            case StatusCodes.NOT_FOUND:
              notify.message = `${translate("users.create_error")}`;
              notify.type = "error";
              props.notificationService?.notify(notify);
              break;
            default: {
              const errorNotify = handleHttpError(
                data,
                `${translate("users.create_error")}`
              );
              props.notificationService?.notify(errorNotify);
            }
          }
        }
      },
    }
  );

  const onFormSubmit = (formData: UserFormModel) => {
    const roleKey = getKey(state.userRoleOptions, formData.role);
    // EVES-5268: Use API-confirmed admin role for global site assignment on submit
    // Author: Abhishek Bharti
    // Date: 08-04-2026
    const isGlobalRole =
      roleKey === UpdatedUserRole.SUPER_ADMIN || state.isSelectedRoleAdmin;

    const rawSelectedIDs = formData.assignedSites?.map((s: any) => s.id) || [];
    const selectedIDs = Array.from(new Set(rawSelectedIDs)).filter(id => !!id);

    const userFormData: any = {
      ...formData,
      role: roleKey,
      locale: getKey(languageOptions, formData.language) || "en_US",
      name: formData.name?.toUpperCase(),
      siteIDs: selectedIDs.length > 0 ? selectedIDs : [""]
    };

    // --- PASSWORD & CLEANUP ---
    if (formData.passwords?.password && formData.passwords.password.trim() !== "") {
      userFormData.password = formData.passwords.password;
    } else {
      delete userFormData.password;
    }

    delete userFormData.passwords;
    delete userFormData.assignedSites;
    delete userFormData.language;
    delete userFormData.site;

    if (isGlobalRole) {
      userFormData.siteIDs = [""]; // Global
    } else {
      userFormData.siteIDs = selectedIDs.length > 0 ? selectedIDs : [""];
    }

    if (props.userId) {
      userFormData.id = props.userId;
      userFormData.status = getKey(statusOptions, formData.status);
      // Pass the context exactly like addUser
      editUser({
        ...userFormData,
        _selectedSiteIDs: selectedIDs,
        _isGlobalRole: isGlobalRole
      });
    } else {
      delete userFormData.id;
      userFormData.status = "";
      // Pass context to mutation
      addUser({
        ...userFormData,
        _selectedSiteIDs: selectedIDs,
        _isGlobalRole: isGlobalRole
      });
    }
  };

  const onHandelClose = () => {
    if (!!Object.keys(userForm.formState?.dirtyFields).length === true) {
      //based on the formValidity confirmation Dialogtype changes
      if (userForm.formState.isValid) {
        props.dialogService?.showConfirm(
          {
            confirmType: "DIRTY_CHANGE_CLOSE",
          },
          (result) => {
            if (result === "SAVE") {
              userForm.handleSubmit(onFormSubmit)();
            } else if (result === "CLOSE") {
              props.onFormClose();
              props.resetUserForm("");
            }
          }
        );
      } else {
        props.dialogService?.showConfirm(
          {
            confirmType: "INVALID_CHANGE_CLOSE",
          },
          (result: string) => {
            if (result === "CLOSE") {
              props.onFormClose();
              props.resetUserForm("");
            }
          }
        );
      }
    } else {
      props.onFormClose();
      props.resetUserForm("");
    }
  };

  return (
    <>
      <EvesModal
        maxWidth={"1200px"}
        isOpen={props.showUserForm}
        onHandleSave={userForm.handleSubmit(onFormSubmit)}
        onHandleClose={onHandelClose}
        isSaveDisabled={!isFormValid}
        tabsConfig={tabsConfig}
        modalType="tabs"
        isSaveHidden={false}
        data-cy="userFormModal"
        scrollButtons={"auto"}
      ></EvesModal>

      {isSiteLookupOpen && (
        <LookupComponent
          title={translate("sites.select_sites")}
          showLookupModal={isSiteLookupOpen}
          onModalClose={() => setSiteLookupOpen(false)}
          selectionMode="multiple" // This enables the multi-select checkboxes
          entityType={LookupEntityType.SITE}
          onSelect={onSiteSelect}
          defaultSortColumn="name"
          params={[{ key: 'Issuer', value: true }]}
          preSelectedData={currentAssignedSites}
        />
      )}

    </>
  );
};

const mapStateToProps = (state: any) => ({
  userInfo: state.userContext.userInfo,
});

export default connect(mapStateToProps)(
  withContext(
    withUseFormHook(UserForm, {
      schema: formSchema,
      defaultValues: { ...new UserFormModel() },
    })
  )
);
